import React, { useState, useEffect } from "react";
import { Table, Button, Popover, Dropdown, Menu } from "antd";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from "chart.js";
import { Calendar, Download, Filter } from "react-feather";
import DatePicker from "react-bootstrap-daterangepicker";
import moment from "moment";
import PrintReportPreview from "../../PrintReportPreview";
import exportService from "../../../utils/exportService"; // Assuming exportService is available
import Swal from "sweetalert2";

// Get business date from localStorage or current date
const getBusinessDate = () => {
  const storedBusinessDate = localStorage.getItem("business_date");
  return storedBusinessDate && moment(storedBusinessDate).isValid()
    ? moment(storedBusinessDate)
    : moment(); // Fallback to current date (July 29, 2025, 04:07 PM IST)
};

const RoomNoWiseSales = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [roomNumberData, setRoomNumberData] = useState([]);
  const [applicableTaxes, setApplicableTaxes] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const businessDate = getBusinessDate();
  const [dateRange, setDateRange] = useState({
    startDate: moment(businessDate).startOf("month"), // Start of month up to business date
    endDate: businessDate.clone().startOf("day"), // Set to business date
  });
  const [filterVisible, setFilterVisible] = useState(false);
  const [reportTitle, setReportTitle] = useState(t("room_number_wise"));
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [exportFooter, setExportFooter] = useState([]);
  const [totalColumns, setTotalColumns] = useState([]);
  const currency = localStorage.getItem("currencycode") || "₹";
  const apiURL = process.env.REACT_APP_API_URL || "http://localhost:4000";

  // Download menu with functional handlers
  const downloadMenu = (
    <Menu>
      <Menu.Item key="xls" onClick={() => handleExport("xls")}>
        {t("export_xls")}
      </Menu.Item>
      <Menu.Item key="csv" onClick={() => handleExport("csv")}>
        {t("export_csv")}
      </Menu.Item>
      <Menu.Item key="preview" onClick={() => handleExport("preview")}>
        {t("preview")}
      </Menu.Item>
    </Menu>
  );

  // Filter content (placeholder)
  const filterContent = (
    <div style={{ padding: 16, width: 250 }}>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", marginBottom: 8, fontWeight: "bold" }}>
          <i className="fas fa-building" style={{ marginRight: 8 }}></i> {t("corporate_company")}
        </label>
        {/* Add SelectCorporate or similar component here if needed */}
      </div>
    </div>
  );

  const handleDateRangeChange = (event, picker) => {
    const newStartDate = moment(picker.startDate).startOf("day");
    const newEndDate = moment(picker.endDate).startOf("day");
    if (!newStartDate.isValid() || !newEndDate.isValid()) {
      Swal.fire({ title: t("error"), text: t("invalid_date_selected"), icon: "error" });
      return;
    }
    if (newStartDate.isAfter(newEndDate)) {
      Swal.fire({ title: t("error"), text: t("start_date_after_end_date"), icon: "error" });
      return;
    }
    if (newEndDate.isAfter(businessDate)) {
      Swal.fire({ title: t("error"), text: t("future_date_restricted"), icon: "error" });
      return;
    }
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
  };

  const handleDateChange = (param) => {
    setDateRange((prevRange) => {
      let newRange;
      if (typeof param === "string") {
        if (param === "prev") {
          newRange = {
            startDate: prevRange.startDate.clone().subtract(1, "day").startOf("day"),
            endDate: prevRange.endDate.clone().subtract(1, "day").startOf("day"),
          };
        } else if (param === "next") {
          newRange = {
            startDate: prevRange.startDate.clone().add(1, "day").startOf("day"),
            endDate: prevRange.endDate.clone().add(1, "day").startOf("day"),
          };
        } else {
          return prevRange;
        }
      } else {
        const { start, end } = param;
        newRange = {
          startDate: moment(start).startOf("day"),
          endDate: moment(end).startOf("day"),
        };
      }

      if (!newRange.startDate.isValid() || !newRange.endDate.isValid()) {
        Swal.fire({ title: t("error"), text: t("invalid_date_selected"), icon: "error" });
        return prevRange;
      }
      if (newRange.startDate.isAfter(newRange.endDate)) {
        Swal.fire({ title: t("error"), text: t("start_date_after_end_date"), icon: "error" });
        return prevRange;
      }
      if (newRange.endDate.isAfter(businessDate)) {
        Swal.fire({ title: t("error"), text: t("future_date_restricted"), icon: "error" });
        return prevRange;
      }

      fetchRoomNumberWiseData(newRange.startDate, newRange.endDate);
      return newRange;
    });
  };

  const fetchRoomNumberWiseData = async (startDate, endDate) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error(t("no_auth_token"));

      const response = await axios.get(`${apiURL}/reports-daily/roomno-wise-summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: startDate.format("YYYY-MM-DD"),
          endDate: endDate.format("YYYY-MM-DD"),
          page: 1,
          pageSize: 100,
        },
      });

      console.log("API Response:", response.data);
      if (response.data.success) {
        setRoomNumberData(response.data.report || []);
        setApplicableTaxes(response.data.applicableTaxes || []);
      } else {
        throw new Error(response.data.message || t("fetch_failed"));
      }
    } catch (error) {
      console.error("Error fetching Room Number Wise Report:", error.message);
      Swal.fire({ title: t("error"), text: t("fetch_failed"), icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoomNumberWiseData(dateRange.startDate, dateRange.endDate);
  }, [dateRange, apiURL]);

  // Define columns with new "Total Revenue" column
  const columns = [
    {
      title: t("room_no"),
      dataIndex: "room_no",
      key: "room_no",
      sorter: (a, b) => b.nights_sold - a.nights_sold,
    },
    {
      title: t("nights_sold"),
      dataIndex: "nights_sold",
      key: "nights_sold",
      sorter: (a, b) => b.nights_sold - a.nights_sold,
    },
    {
      title: t("actual_price"),
      dataIndex: "actual_price",
      key: "actual_price",
      render: (text) => `${currency}${Number(text || 0).toFixed(2)}`,
      sorter: (a, b) => a.actual_price - b.actual_price,
    },
    {
      title: t("commission_discount"),
      dataIndex: "discount",
      key: "discount",
      render: (text) => `${currency}${Number(text || 0).toFixed(2)}`,
      sorter: (a, b) => a.discount - b.discount,
    },
    {
      title: t("total_revenue"),
      dataIndex: "total_revenue",
      key: "total_revenue",
      render: (text, record) => `${currency}${((record.actual_price || 0) - (record.discount || 0)).toFixed(2)}`,
      sorter: (a, b) => ((a.actual_price || 0) - (a.discount || 0)) - ((b.actual_price || 0) - (b.discount || 0)),
    },
    ...applicableTaxes.map((taxName) => ({
      title: `${taxName} (${currency})`,
      dataIndex: taxName,
      key: taxName,
      render: (text) => `${currency}${Number(text || 0).toFixed(2)}`,
      sorter: (a, b) => (a[taxName] || 0) - (b[taxName] || 0),
    })),
{
  title: t("net_revenue"),
  dataIndex: "net_revenue",
  key: "net_revenue",
  render: (text, record) => {
    const totalRevenue = (record.actual_price || 0) - (record.discount || 0); // Total Revenue (actual_price - discount)
    const taxTotal = applicableTaxes.reduce((sum, taxName) => sum + (record[taxName] || 0), 0); // Sum of all taxes
    const netRevenue = totalRevenue + taxTotal; // Net Revenue = Total Revenue + Taxes
    return `${currency}${netRevenue.toFixed(2)}`;
  },
  sorter: (a, b) => {
    const aTotalRevenue = (a.actual_price || 0) - (a.discount || 0);
    const bTotalRevenue = (b.actual_price || 0) - (b.discount || 0);
    const aTaxTotal = applicableTaxes.reduce((sum, taxName) => sum + (a[taxName] || 0), 0);
    const bTaxTotal = applicableTaxes.reduce((sum, taxName) => sum + (b[taxName] || 0), 0);
    const aNetRevenue = aTotalRevenue + aTaxTotal;
    const bNetRevenue = bTotalRevenue + bTaxTotal;
    return aNetRevenue - bNetRevenue;
  },
  onCell: () => ({ style: { backgroundColor: "#e2e6ea" } }),
},
  ];

  const handleExport = (format) => {
    if (!exportData.length && format !== "print") {
      Swal.fire({ title: t("error"), text: t("no_data_export"), icon: "error" });
      return;
    }
    const timestamp = new Date().toISOString().replace(/[:.-]/g, "");
    const filename = `room_no_wise_${dateRange.startDate.format("YYYY-MM-DD")}_${dateRange.endDate.format("YYYY-MM-DD")}_${timestamp}`;
    
    if (format === "xls") {
      exportService.exportToXLS(exportData, exportHeaders, `${filename}.xlsx`, reportTitle);
    } else if (format === "csv") {
      exportService.exportToCSV(exportData, exportHeaders, `${filename}.csv`);
    } else if (format === "preview") {
      setPreviewVisible(true);
    }
  };

  const totals = roomNumberData.reduce(
    (acc, item) => {
      const taxTotal = applicableTaxes.reduce((sum, taxName) => sum + (item[taxName] || 0), 0);
      return {
        nights_sold: acc.nights_sold + (item.nights_sold || 0),
        actual_price: acc.actual_price + (item.actual_price || 0),
        discount: acc.discount + (item.discount || 0),
        total_revenue: acc.total_revenue + ((item.actual_price || 0) - (item.discount || 0)),
        net_revenue: acc.net_revenue + taxTotal,
        ...applicableTaxes.reduce((taxAcc, taxName) => ({
          ...taxAcc,
          [taxName]: (taxAcc[taxName] || 0) + (item[taxName] || 0),
        }), acc),
      };
    },
    {
      nights_sold: 0,
      actual_price: 0,
      discount: 0,
      total_revenue: 0,
      net_revenue: 0,
      ...applicableTaxes.reduce((acc, taxName) => ({ ...acc, [taxName]: 0 }), {}),
    }
  );

  // Sort and limit chart data to top 10 rooms by revenue, ordered by nights_sold
  const top10Rooms = [...roomNumberData]
    .sort((a, b) => b.net_revenue - a.net_revenue || b.nights_sold - a.nights_sold)
    .slice(0, 10);

  const chartData = {
    labels: top10Rooms.map((item) => item.room_no || "N/A"),
    datasets: [
      {
        type: "bar",
        label: t("net_revenue"),
        data: top10Rooms.map((item) => {
          const taxTotal = applicableTaxes.reduce((sum, taxName) => sum + (item[taxName] || 0), 0);
          return taxTotal || 0;
        }),
        backgroundColor: "#28a745",
        yAxisID: "y",
      },
      {
        type: "bar",
        label: t("nights_sold"),
        data: top10Rooms.map((item) => item.nights_sold || 0),
        backgroundColor: "#007bff",
        yAxisID: "y1",
      },
      {
        type: "line",
        label: t("nights_sold_trend"),
        data: top10Rooms.map((item) => item.nights_sold || 0),
        borderColor: "#ff6347",
        backgroundColor: "#ff6347",
        fill: false,
        tension: 0.4,
        yAxisID: "y1",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        type: "linear",
        display: true,
        position: "left",
        title: { display: true, text: `${t("net_revenue")} (${currency})` },
        beginAtZero: true,
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        title: { display: true, text: t("nights_sold") },
        beginAtZero: true,
        grid: { drawOnChartArea: false },
      },
      x: {
        title: { display: true, text: t("room_no") },
      },
    },
    plugins: {
      legend: { position: "top" },
      title: { display: true, text: t("top_performer_rooms") },
    },
  };

  useEffect(() => {
    if (roomNumberData.length > 0) {
      setExportData(roomNumberData);
      setExportHeaders(columns.map((col) => ({ key: col.key, label: col.title })));
      setExportFooter([
        { key: "nights_sold", value: totals.nights_sold, currency: false },
        { key: "actual_price", value: totals.actual_price, currency: true },
        { key: "discount", value: totals.discount, currency: true },
        { key: "total_revenue", value: totals.total_revenue, currency: true },
        ...applicableTaxes.map((taxName) => ({
          key: taxName,
          value: totals[taxName],
          currency: true,
        })),
        { key: "net_revenue", value: totals.net_revenue, currency: true },
      ]);
      setTotalColumns(["nights_sold", "actual_price", "discount", "total_revenue", ...applicableTaxes, "net_revenue"]);
      setReportTitle(t("room_number_wise"));
    }
  }, [roomNumberData, applicableTaxes]);

  const renderView = () => (
    <>
      <div className="row mb-4">
        <div className="col-md-12">
          <div className="card" style={{ borderRadius: "0", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}>
            <div className="card-header" style={{ backgroundColor: "#fff", padding: "12px 16px", borderBottom: "none", position: "relative" }}>
              <h5 className="mb-0 d-flex align-items-center" style={{ fontSize: "14px", fontWeight: 600, color: "#333", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {t("top_performer_rooms")} (Total Nights: {totals.nights_sold})
              </h5>
              <div style={{ position: "absolute", bottom: "0", left: "0", width: "100%", height: "4px", backgroundColor: "#28a745" }} />
            </div>
            <div className="card-body" style={{ padding: "10px", height: "300px" }}>
              <div style={{ height: "100%", width: "100%" }}>
                <Bar data={chartData} options={chartOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row g-0">
        <div className="col-md-12">
          <div className="card" style={{ borderRadius: "0", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}>
            <div className="card-header" style={{ backgroundColor: "#fff", padding: "12px 16px", borderBottom: "none", position: "relative" }}>
              <h5 className="mb-0 d-flex align-items-center" style={{ fontSize: "14px", fontWeight: 600, color: "#333", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {t("room_number_wise")} (Total Nights: {totals.nights_sold})
              </h5>
              <div style={{ position: "absolute", bottom: "0", left: "0", width: "100%", height: "4px", backgroundColor: "#007bff" }} />
            </div>
            <div className="card-body p-0">
              <Table
                columns={columns}
                dataSource={roomNumberData.sort((a, b) => b.nights_sold - a.nights_sold)} // Sort by nights_sold descending
                rowKey="room_no"
                pagination={false}
                bordered
                className="daybook-table"
                style={{ margin: 0 }}
                summary={() => (
                  <Table.Summary.Row style={{ backgroundColor: "#e6f4ea", fontWeight: "700", fontSize: "15px" }}>
                    <Table.Summary.Cell>{t("total")}</Table.Summary.Cell>
                    <Table.Summary.Cell>{totals.nights_sold}</Table.Summary.Cell>
                    <Table.Summary.Cell>{currency}{totals.actual_price.toFixed(2)}</Table.Summary.Cell>
                    <Table.Summary.Cell>{currency}{totals.discount.toFixed(2)}</Table.Summary.Cell>
                    <Table.Summary.Cell>{currency}{totals.total_revenue.toFixed(2)}</Table.Summary.Cell>
                    {applicableTaxes.map((taxName, index) => (
                      <Table.Summary.Cell key={index}>{currency}{totals[taxName].toFixed(2)}</Table.Summary.Cell>
                    ))}
                    <Table.Summary.Cell style={{ backgroundColor: "#e2e6ea" }}>{currency}{totals.net_revenue.toFixed(2)}</Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons px-3 py-2 bg-white border rounded">
          <div className="row align-items-center flex-nowrap">
            <div className="col-auto d-flex align-items-center gap-3">
              <h2
                className="room-title mb-0"
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  width: "400px",
                  borderBottom: "2px solid #007bff",
                  paddingBottom: "4px",
                }}
              >
                {t("ROOM_SOLD")}
              </h2>
              <DatePicker
                initialSettings={{
                  singleDatePicker: false,
                  showDropdowns: true,
                  startDate: dateRange.startDate.toDate(),
                  endDate: dateRange.endDate.toDate(),
                  ranges: {
                    "Today": [moment().startOf("day").toDate(), moment().endOf("day").toDate()],
                    "Yesterday": [moment().subtract(1, "days").startOf("day").toDate(), moment().subtract(1, "days").endOf("day").toDate()],
                    "This Week": [moment().startOf("week").toDate(), moment().endOf("week").toDate()],
                    "Last Week": [moment().subtract(1, "week").startOf("week").toDate(), moment().subtract(1, "week").endOf("week").toDate()],
                    "This Month": [moment().startOf("month").toDate(), businessDate.toDate()],
                    "Last Month": [moment().subtract(1, "month").startOf("month").toDate(), moment().subtract(1, "month").endOf("month").toDate()],
                  },
                  locale: {
                    format: "DD MMM YYYY",
                    applyLabel: "Select",
                    cancelLabel: "Cancel",
                  },
                  minDate: moment().subtract(1, "year").toDate(), // Allow past dates
                  maxDate: businessDate.toDate(), // Restrict to business date
                }}
                onCallback={(start, end) => handleDateChange({ start, end })}
              >
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={`${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format("DD MMM YYYY")}`}
                    style={{
                      width: "220px",
                      height: "38px",
                      fontSize: "16px",
                      fontWeight: "bold",
                      textAlign: "center",
                      padding: "0 10px",
                      borderRight: "none",
                      backgroundColor: "#ffffff",
                      borderRadius: "4px 0 0 4px",
                    }}
                    readOnly
                  />
                  <div className="input-group-append">
                    <span
                      className="input-group-text"
                      style={{
                        height: "38px",
                        backgroundColor: "#fff",
                        borderLeft: "none",
                        borderRadius: "0 4px 4px 0",
                        cursor: "pointer",
                        padding: "0 10px",
                      }}
                    >
                      <i className="fa fa-chevron-down" style={{ color: "#495057" }} />
                    </span>
                  </div>
                </div>
              </DatePicker>
            </div>
            <div className="col-auto ms-auto d-flex align-items-center gap-3">
              <Dropdown overlay={downloadMenu} trigger={["click"]} placement="bottomRight">
                <Button type="primary" className="d-flex align-items-center" style={{ height: "38px" }}>
                  <Download size={16} className="me-2" /> {t("download")}
                </Button>
              </Dropdown>
            </div>
          </div>
        </div>
        {loading ? (
          <div className="text-center my-5">
            <div className="loader">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
        ) : (
          renderView()
        )}
        <PrintReportPreview
          visible={previewVisible}
          onClose={() => setPreviewVisible(false)}
          reportTitle={reportTitle}
          propertyName=""
          data={exportData}
          headers={exportHeaders}
          footerData={exportFooter}
          totalColumns={totalColumns}
          apiEndpoint="transactions/generate-pdf"
          apiUrl={apiURL}
        />
        <style jsx>{`
          .loader {
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .dot {
            width: 10px;
            height: 10px;
            background-color: #007bff;
            border-radius: 50%;
            margin: 0 5px;
            animation: dot-bounce 1.4s infinite ease-in-out both;
          }
          .dot:nth-child(1) {
            animation-delay: -0.32s;
          }
          .dot:nth-child(2) {
            animation-delay: -0.16s;
          }
          @keyframes dot-bounce {
            0%, 80%, 100% { transform: scale(0); }
            40% { transform: scale(1); }
          }
          .form-control {
            border-radius: 4px;
            border: 1px solid #d9d9d9;
            padding: 6px 12px;
            font-size: 14px;
          }
          .dropdown-menu .active {
            background-color: #007bff;
            color: white;
          }
          .card {
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          @media (max-width: 768px) {
            .room-title {
              width: 200px !important;
            }
            .input-group {
              width: 180px !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default RoomNoWiseSales;